/*
 * @Author: Zhouqi
 * @Date: 2026-01-05 14:52:23
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-01-05 14:52:41
 */
/**
 * WebP 工具函数
 * 提供检测和首帧提取功能
 */

/**
 * 置信度等级
 */
export type ConfidenceLevel = 'high' | 'low';

/**
 * 检测结果接口
 */
export interface WebPDetectionResult {
  /** 是否为单帧图片 */
  isSingleFrame: boolean;
  /** 是否为动画 */
  isAnimated: boolean;
  /** 帧数 */
  frameCount: number;
  /** 置信度等级 */
  confidence: ConfidenceLevel;
}

/**
 * 检测 WebP 图片是否为单帧
 * @param input - File 对象、Blob 或 URL 字符串
 * @returns 检测结果
 *
 * @example
 * ```typescript
 * const file = document.querySelector('input').files[0];
 * const result = await isWebPSingleFrame(file);
 *
 * if (result.isSingleFrame) {
 *   console.log('这是单帧图片');
 * } else {
 *   console.log(`这是动画，共 ${result.frameCount} 帧`);
 * }
 * ```
 */
export async function isWebPSingleFrame(
  input: File | Blob | string,
): Promise<WebPDetectionResult> {
  const bytes = await getBytes(input);

  // 检查 WebP 文件头
  if (
    bytes[0] !== 0x52 ||
    bytes[1] !== 0x49 ||
    bytes[2] !== 0x46 ||
    bytes[3] !== 0x46 ||
    bytes[8] !== 0x57 ||
    bytes[9] !== 0x45 ||
    bytes[10] !== 0x42 ||
    bytes[11] !== 0x50
  ) {
    throw new Error('不是有效的 WebP 文件');
  }

  let isAnimated = false;
  let hasVP8X = false;
  let hasANIM = false;
  let hasANMF = false;
  let hasVP8 = false;
  let hasVP8L = false;
  let anmfCount = 0;
  let pos = 12;
  let chunkCount = 0;

  // 遍历所有 chunks
  while (pos < bytes.length - 8 && chunkCount < 10000) {
    const chunkType = String.fromCharCode(
      bytes[pos],
      bytes[pos + 1],
      bytes[pos + 2],
      bytes[pos + 3],
    );
    const chunkSize =
      bytes[pos + 4] |
      (bytes[pos + 5] << 8) |
      (bytes[pos + 6] << 16) |
      (bytes[pos + 7] << 24);

    if (chunkSize < 0 || chunkSize > bytes.length) break;

    chunkCount++;

    // VP8X 扩展块
    if (chunkType === 'VP8X') {
      hasVP8X = true;
      if (pos + 8 < bytes.length) {
        const flags = bytes[pos + 8];
        const hasAnimation = (flags & 0x02) !== 0;
        if (hasAnimation) {
          isAnimated = true;
        }
      }
    }
    // ANIM 动画块
    else if (chunkType === 'ANIM') {
      hasANIM = true;
      isAnimated = true;
    }
    // ANMF 动画帧
    else if (chunkType === 'ANMF') {
      hasANMF = true;
      isAnimated = true;
      anmfCount++;
    }
    // VP8 或 VP8L（简单格式）
    else if (chunkType === 'VP8 ') {
      hasVP8 = true;
    } else if (chunkType === 'VP8L') {
      hasVP8L = true;
    }

    pos += 8 + chunkSize + (chunkSize % 2);
  }

  // 计算置信度
  let confidenceScore = 0;
  if (isAnimated) {
    if (hasVP8X) confidenceScore += 2;
    if (hasANIM) confidenceScore += 2;
    if (hasANMF && anmfCount > 1) confidenceScore += 1;
    if (anmfCount === 0) confidenceScore = Math.max(1, confidenceScore - 2);
  } else {
    if (hasVP8 || hasVP8L) confidenceScore += 3;
    if (!hasVP8X) confidenceScore += 1;
    if (!hasANIM && !hasANMF) confidenceScore += 1;
  }
  confidenceScore = Math.min(5, confidenceScore);

  // 转换为文字等级
  let confidence: ConfidenceLevel;
  if (confidenceScore >= 3) {
    confidence = 'high';
  } else {
    confidence = 'low';
  }

  return {
    isSingleFrame: !isAnimated,
    isAnimated,
    frameCount: anmfCount,
    confidence,
  };
}

/**
 * 提取 WebP 动画的首帧，返回可上传的 File 对象
 * @param input - File 对象、Blob 或 URL 字符串
 * @param fileName - 输出文件名（可选，默认为 'first-frame.webp'）
 * @returns File 对象，可直接上传到 OSS
 *
 * @example
 * ```typescript
 * const file = document.querySelector('input').files[0];
 * const firstFrameFile = await extractWebPFirstFrame(file, 'thumbnail.webp');
 *
 * // 上传到 OSS
 * await uploadToOSS(firstFrameFile);
 * ```
 */
export async function extractWebPFirstFrame(
  input: File | Blob | string,
  fileName: string = 'first-frame.webp',
): Promise<File> {
  // 创建临时图片加载
  const url = typeof input === 'string' ? input : URL.createObjectURL(input);

  const shouldRevoke = typeof input !== 'string';

  try {
    // 使用 Image + Canvas 方式提取首帧
    const blob = await new Promise<Blob>((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('无法获取 Canvas 上下文');
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('无法生成 Blob'));
              }
            },
            'image/webp',
            0.92,
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('无法加载图片'));
      };

      img.src = url;
    });

    // 清理临时 URL
    if (shouldRevoke) {
      URL.revokeObjectURL(url);
    }

    // 转换为 File 对象
    const file = new File([blob], fileName, { type: 'image/webp' });

    return file;
  } catch (error) {
    if (shouldRevoke) {
      URL.revokeObjectURL(url);
    }
    throw error;
  }
}

/**
 * 判断文件是否为 WebP 格式
 * @param input - File 对象、Blob 或 URL 字符串
 * @returns 是否为 WebP 格式
 *
 * @example
 * ```typescript
 * const file = document.querySelector('input').files[0];
 * const isWebP = await isWebPFile(file);
 *
 * if (isWebP) {
 *   console.log('这是 WebP 文件');
 * } else {
 *   console.log('这不是 WebP 文件');
 * }
 * ```
 */
export async function isWebPFile(input: File | Blob | string): Promise<boolean> {
  try {
    // 如果是 File 或 Blob，先检查 MIME 类型
    if (input instanceof File || input instanceof Blob) {
      if (input.type === 'image/webp') {
        return true;
      }
    }

    // 通过文件头魔数检测
    const bytes = await getBytes(input);

    // WebP 文件头特征：
    // 字节 0-3: "RIFF" (0x52 0x49 0x46 0x46)
    // 字节 8-11: "WEBP" (0x57 0x45 0x42 0x50)
    if (bytes.length < 12) {
      return false;
    }

    const isRIFF =
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;

    const isWEBP =
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;

    return isRIFF && isWEBP;
  } catch (error) {
    // 如果读取出错，返回 false
    return false;
  }
}

/**
 * 获取字节数据
 * @private
 */
async function getBytes(input: File | Blob | string): Promise<Uint8Array> {
  if (input instanceof Blob) {
    const arrayBuffer = await input.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  if (typeof input === 'string') {
    const response = await fetch(input);
    if (!response.ok) {
      throw new Error(`无法获取文件: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  throw new Error('不支持的输入类型');
}
